import { UNITS, type EliteOnboardingStatusResponse } from '../../types/misc.types'
import type { PlusBanner, PlusBannerItem } from './types/plusBanner.types'
import { convertUnitAndValue, roundOffAndFormatNumberWithCurrency } from '../../utils/utils'
import {
  isPriceBasedOfferType,
  isTenureBasedOfferType
} from '../../components/elite/EliteSpecialOfferGiftBanner'
import {
  resolveElitePlansTenureBasedOfferDisplay,
  resolveElitePrimeUpgradeCtaFeeAmount,
  resolveRecommendedElitePrimePlan
} from './elitePlansPrimaryCtaDisplay'
import { resolveElitePrimeUpgradeDetails } from './elitePlansUpgradeDetails'

const MARKET_PLUS_BANNER_ITEM_ID = 'market'

function portfolioFeeLabel(percentage: number): string {
  const formatted = convertUnitAndValue({ value: percentage, unit: UNITS.PERCENTAGE })
  return `${formatted} of your portfolio`
}

function formatSlabPercent(percentage: number): string {
  return convertUnitAndValue({ value: percentage, unit: UNITS.PERCENTAGE })
}

function resolveElitePrimeFeesAmount(status?: EliteOnboardingStatusResponse): number | undefined {
  const plan = resolveRecommendedElitePrimePlan(status)
  return plan?.effectivePlanFees?.amount
}

/**
 * Market comparison only when market price is defined and not cheaper than Elite Prime.
 * If market is lower, hide the market row / market pricing section.
 */
export function shouldShowElitePlansMarketPricing(
  status?: EliteOnboardingStatusResponse
): boolean {
  const marketPrice = status?.marketPricingDetails?.marketPrice?.amount
  const elitePrimeFees = resolveElitePrimeFeesAmount(status)
  if (marketPrice == null || elitePrimeFees == null) {
    return false
  }
  return marketPrice >= elitePrimeFees
}

function elitePrimeRow(item: PlusBannerItem, status: EliteOnboardingStatusResponse): PlusBannerItem {
  const plan = resolveRecommendedElitePrimePlan(status)
  const effectiveFees = plan?.effectivePlanFees?.amount
  const upgradePricing = resolveElitePrimeUpgradeDetails(status)?.upgradePricingDetails
  const displayFees = upgradePricing
    ? resolveElitePrimeUpgradeCtaFeeAmount(effectiveFees, upgradePricing)
    : (effectiveFees ?? 0)
  const effectiveSlab = plan?.effectiveSlabPricePercentage?.amount ?? 0
  const baseSlab = plan?.baseSlabPricePercentage?.amount ?? 0
  const isPriceBased = isPriceBasedOfferType(plan?.offerType)
  const isTenureBased = isTenureBasedOfferType(plan?.offerType)
  const showStruckSlab = isPriceBased && baseSlab > 0 && effectiveSlab > 0 && baseSlab !== effectiveSlab
  const tenureBasedOffer = isTenureBased ? resolveElitePlansTenureBasedOfferDisplay(status) : null

  return {
    ...item,
    sublabel: tenureBasedOffer?.plusBannerSublabel ?? item.sublabel,
    price: `${roundOffAndFormatNumberWithCurrency(displayFees)}`,
    priceSuffix: '+GST',
    feeLabel: showStruckSlab
      ? formatSlabPercent(effectiveSlab)
      : portfolioFeeLabel(effectiveSlab),
    feeLabelStrike: showStruckSlab ? formatSlabPercent(baseSlab) : undefined,
    feeLabelSuffix: showStruckSlab ? ' of your portfolio' : undefined
  }
}

function marketRow(item: PlusBannerItem, status: EliteOnboardingStatusResponse): PlusBannerItem {
  const marketPrice = status?.marketPricingDetails?.marketPrice?.amount ?? 0
  const portfolioPercentage = status?.marketPricingDetails?.portfolioPercentage?.amount ?? 0

  return {
    ...item,
    price: `${roundOffAndFormatNumberWithCurrency(marketPrice)}`,
    priceSuffix: '/yr',
    feeLabel: portfolioFeeLabel(portfolioPercentage)
  }
}

export function resolveElitePlansPlusBanner(
  plusBanner: PlusBanner | undefined,
  status: EliteOnboardingStatusResponse | undefined
): PlusBanner | undefined {
  if (!plusBanner || !status) {
    return plusBanner
  }

  const showMarket = shouldShowElitePlansMarketPricing(status)

  return {
    ...plusBanner,
    items: plusBanner.items.flatMap((item) => {
      if (item.id === MARKET_PLUS_BANNER_ITEM_ID) {
        if (!showMarket) return []
        return [marketRow(item, status)]
      }
      if (item.id === 'elite-prime') return [elitePrimeRow(item, status)]
      return [item]
    })
  }
}
